import {useEffect, useState} from 'react';
import {useDebounce} from 'ahooks';
import type {InstagramReelMusic} from '@Components/social-media/publish-params.types';
import {getCachedInstagramAudioTracks, searchInstagramAudio} from '@Libraries/instagram-audio-search.library';
import type {ReelMusicQueryResult, ReelMusicSearchResults, ReelMusicSearchView} from './reel-music-search.types';
import {REEL_MUSIC_SEARCH_DEBOUNCE_MS, ReelMusicSearchState} from './reel-music-search.types';

export function useReelMusicSearch(accountId: number, chosenReelMusic: InstagramReelMusic | null): ReelMusicSearchResults {
  const [searchQuery, setSearchQuery] = useState(chosenReelMusic?.title ?? '');
  const trimmedQuery = searchQuery.trim();
  const debouncedSearchQuery = useDebounce(trimmedQuery, {wait: REEL_MUSIC_SEARCH_DEBOUNCE_MS});
  const [lastResult, setLastResult] = useState<ReelMusicQueryResult | null>(() => getChosenSongResult(chosenReelMusic));
  const [searchAttempt, setSearchAttempt] = useState(0);

  useEffect(() => {
    let isStale = false;

    const loadTracks = async (): Promise<void> => {
      try {
        const tracks = await searchInstagramAudio(accountId, debouncedSearchQuery);

        if (!isStale) {
          setLastResult({query: debouncedSearchQuery, tracks, searchState: ReelMusicSearchState.LOADED});
        }
      } catch {
        if (!isStale) {
          setLastResult({query: debouncedSearchQuery, tracks: [], searchState: ReelMusicSearchState.ERROR});
        }
      }
    };

    if (debouncedSearchQuery) {
      void loadTracks();
    }

    return (): void => {
      isStale = true;
    };
  }, [accountId, debouncedSearchQuery, searchAttempt]);

  const searchView = getSearchView(accountId, trimmedQuery, lastResult);

  return {
    searchQuery,
    hasSearchQuery: trimmedQuery !== '',
    tracks: searchView.tracks,
    searchState: searchView.searchState,
    changeSearchQuery: setSearchQuery,
    retrySearch: (): void => {
      setLastResult(null);
      setSearchAttempt((attempt) => {
        return attempt + 1;
      });
    },
  };
}

function getSearchView(accountId: number, query: string, lastResult: ReelMusicQueryResult | null): ReelMusicSearchView {
  const cachedTracks = getCachedInstagramAudioTracks(accountId, query);

  if (cachedTracks) {
    return {tracks: cachedTracks, searchState: ReelMusicSearchState.LOADED};
  }

  if (lastResult?.query === query) {
    return lastResult;
  }

  return {tracks: [], searchState: ReelMusicSearchState.LOADING};
}

function getChosenSongResult(chosenReelMusic: InstagramReelMusic | null): ReelMusicQueryResult | null {
  if (!chosenReelMusic) {
    return null;
  }

  return {query: chosenReelMusic.title.trim(), tracks: [chosenReelMusic], searchState: ReelMusicSearchState.LOADED};
}
